import { z } from 'zod';
import { mapCatalogEntry, TOTAL_MAP_COUNT } from '../catalog';
import { openWorldCardFlagsSchema } from './cards';
import { OPEN_WORLD_RATING_MAX, OPEN_WORLD_RATING_MIN } from './content';

/**
 * **Os mobs do mundo aberto: os primeiros seres vivos de um quadrante.**
 *
 * ## O que os separa de um cometa e de um planeta
 *
 * Tudo. Um recurso é rocha inerte que se parte; um mob **persegue, ataca e
 * morre**. Ele tem vida, alvo, cadência e uma morte com autor — e por isso não
 * partilha a fundação de `resources.ts`, que existe para corpos imóveis feitos
 * de partes.
 *
 * O que ele partilha com eles é a disciplina: identidade estável por vaga,
 * geração por vida, relógio de parede para o renascimento, área de interesse a
 * filtrar antes da trama, e uma recompensa idempotente com chave derivada.
 *
 * ## De onde vêm os números
 *
 * **Do catálogo da caça, e de lado nenhum.** A vida por mapa é
 * `mobHealthForMap`, a escala é `mobScaleBpsForMap`, o raio de colisão é a
 * fração canónica do tamanho de exibição, o dano de contacto é `mobLeakDamage`
 * e o ouro é a fração de um por cada duas mortes. Copiar coeficientes para
 * `open-world/` teria criado uma segunda tabela a divergir da primeira.
 *
 * ## O que este ficheiro decide, e o que não
 *
 * Aqui está o **contrato**: o relógio do renascimento, a forma pública e a
 * derivação da chave de recompensa. Alcance de deteção, distância de abandono e
 * raio de regresso são equilíbrio, vivem em `open-world/room/balance.ts` e são
 * provisórios.
 */

/* -------------------------------------------------------------------------- */
/*  O relógio                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * **Cinco minutos de relógio real depois de morrer, e o mob volta.**
 *
 * Tempo de parede e não tempo observado: um quadrante vazio não corre tiques, e
 * um mob que só ressuscitasse enquanto alguém o estivesse a ver seria um mob
 * que nunca volta. O prazo sobrevive a uma suspensão, a uma reconstrução do
 * objeto durável e a uma reconexão, e não é reiniciado por nenhuma delas.
 */
export const OPEN_WORLD_MOB_RESPAWN_MS = 5 * 60 * 1_000;

/* -------------------------------------------------------------------------- */
/*  A composição por perigo                                                   */
/* -------------------------------------------------------------------------- */

/**
 * **Quantos mapas ATIVOS o catálogo tem**, contados e não escritos.
 *
 * Ativo quer dizer planeado para o alfa fechado: os mapas que a progressão
 * atravessa e para os quais existe — ou existirá — um alienígena. Os reservados
 * depois do alfa não entram, porque um quadrante que sorteasse um deles teria
 * mobs de um mapa que ninguém pode jogar.
 *
 * Contado por varrimento do catálogo em vez de lido de `CLOSED_ALPHA_MAP_COUNT`
 * de propósito: a constante diz uma intenção, e o varrimento diz o que as
 * entradas realmente afirmam. Se as duas alguma vez discordarem, é a segunda
 * que descreve o jogo.
 */
export const OPEN_WORLD_MOB_ACTIVE_MAP_COUNT = ((): number => {
  let count = 0;
  for (let index = 1; index <= TOTAL_MAP_COUNT; index += 1) {
    if (mapCatalogEntry(index).plannedForClosedAlpha) {
      count += 1;
    }
  }
  return count;
})();

/** Quantas faixas de perigo existem. Derivado dos próprios graus, 1..5. */
export const OPEN_WORLD_MOB_DANGER_BANDS = OPEN_WORLD_RATING_MAX - OPEN_WORLD_RATING_MIN + 1;

/**
 * **Quantos mapas ativos esta derivação foi decidida a cobrir.**
 *
 * Uma âncora deliberada, e não uma duplicação. A partição depende da contagem
 * do catálogo, portanto acrescentar ou retirar um mapa ativo **muda a espécie
 * de mobs em quadrantes que já existem** — e um mundo não deve mudar de
 * inimigos porque alguém acrescentou uma entrada.
 *
 * Quando o catálogo mudar, isto tem de ser mudado com ele, de propósito, no
 * mesmo commit: é essa a decisão que a discordância obriga a tomar. Os mobs já
 * nascidos não se mexem de qualquer forma, porque a espécie de cada um está
 * escrita na instantânea — o que muda é o que nasce a partir daí.
 */
export const OPEN_WORLD_MOB_SPECIES_MAP_COUNT = 20;

/**
 * **A faixa de mapas de um grau de perigo**: o primeiro, e quantos.
 *
 * A partição cobre TODOS os mapas ativos e não deixa nenhum de fora. Quando a
 * contagem não é divisível pelo número de faixas, o resto vai para as faixas
 * BAIXAS: a periferia ganha variedade, e o grupo mais forte fica um conjunto
 * pequeno e reconhecível em vez de ser diluído.
 *
 * Fracos na periferia, fortes na direção do centro — a mesma direção em que o
 * perigo do quadrante já cresce.
 */
export function openWorldMobMapBand(danger: number): {
  readonly first: number;
  readonly count: number;
} {
  if (OPEN_WORLD_MOB_ACTIVE_MAP_COUNT !== OPEN_WORLD_MOB_SPECIES_MAP_COUNT) {
    /*
     * **Recusa, e não adapta.** Adaptar em silêncio trocaria os inimigos de
     * quadrantes que já existem. Quem mexer no catálogo decide, aqui, o que
     * isso quer dizer para os mundos que já correm.
     *
     * A guarda estava em `openWorldMobMapIndex`, que era o único chamador.
     * Mudou-se com ela para a faixa, que é o que sobrou depois de a escolha
     * dentro dela ter passado para `openWorldMapIndexInBand`.
     */
    throw new RangeError(
      `open world mob species were derived for ${OPEN_WORLD_MOB_SPECIES_MAP_COUNT} active maps; the catalogue now has ${OPEN_WORLD_MOB_ACTIVE_MAP_COUNT}`,
    );
  }
  const band = Math.max(OPEN_WORLD_RATING_MIN, Math.min(OPEN_WORLD_RATING_MAX, Math.trunc(danger)));
  const base = Math.floor(OPEN_WORLD_MOB_ACTIVE_MAP_COUNT / OPEN_WORLD_MOB_DANGER_BANDS);
  const extra = OPEN_WORLD_MOB_ACTIVE_MAP_COUNT % OPEN_WORLD_MOB_DANGER_BANDS;
  const before = band - OPEN_WORLD_RATING_MIN;
  return {
    first: before * base + Math.min(before, extra) + 1,
    count: base + (before < extra ? 1 : 0),
  };
}

/**
 * **De que faixa de perigo um mapa veio.** A inversa da partição.
 *
 * Para a apresentação, que precisa de distinguir um alienígena fraco de um
 * forte enquanto houver um só corpo desenhado. Deriva da mesma partição, e
 * portanto não pode discordar dela.
 */
export function openWorldMobDangerOfMap(mapIndex: number): number {
  for (let band = OPEN_WORLD_RATING_MIN; band <= OPEN_WORLD_RATING_MAX; band += 1) {
    const { first, count } = openWorldMobMapBand(band);
    if (mapIndex >= first && mapIndex < first + count) {
      return band;
    }
  }
  return OPEN_WORLD_RATING_MIN;
}

/* -------------------------------------------------------------------------- */
/*  A identidade da recompensa                                                */
/* -------------------------------------------------------------------------- */

const SEPARATOR = '|';

/**
 * **O id do ouro de uma morte**, derivado e nunca cunhado.
 *
 * Mundo, quadrante, vaga e geração. **A conta não entra**, e é deliberado: uma
 * morte paga uma vez, e a conta é o DESTINATÁRIO que o último golpe decidiu —
 * não parte da identidade do que aconteceu. Se ela entrasse na chave, duas
 * tentativas de pagar a mesma morte a contas diferentes produziriam dois ids, e
 * a idempotência deixaria de proteger o que existe para proteger.
 *
 * Uma geração é uma vida: a mesma vaga que morre outra vez tem outra geração e
 * outra chave.
 */
export function openWorldMobGoldEventId(input: {
  readonly worldId: string;
  readonly slotId: string;
  readonly generation: number;
}): string {
  return ['mob-gold', input.worldId, input.slotId, String(input.generation)].join(SEPARATOR);
}

/**
 * **O id da dívida herdada do acumulador de micro-ouro**, derivado.
 *
 * Migração, mundo, quadrante e conta. Vale UMA vez por conta e por quadrante,
 * por mais vezes que o registo antigo seja lido: dois arranques do mesmo objeto
 * durável produzem o mesmo id, e a operação idempotente da carteira de campo
 * responde ao segundo com o que já tinha escrito.
 *
 * A migração entra na chave de propósito. Ela nomeia o degrau que gerou a
 * dívida — não uma morte, não uma vaga — e uma migração futura que voltasse a
 * dever alguma coisa a alguém teria de nomear a SUA, em vez de colidir com esta.
 */
export function openWorldMobGoldLegacyEventId(input: {
  readonly worldId: string;
  readonly rowIndex: number;
  readonly columnIndex: number;
  readonly accountId: string;
}): string {
  return [
    'mob-gold-carry-v9',
    input.worldId,
    `${input.rowIndex}:${input.columnIndex}`,
    input.accountId,
  ].join(SEPARATOR);
}

/* -------------------------------------------------------------------------- */
/*  A forma pública                                                           */
/* -------------------------------------------------------------------------- */

/** O id público de uma vaga de mob: o id do espaço que ela ocupa. */
export const openWorldMobIdSchema = z.string().min(1).max(64);

/**
 * **Um mob a ENTRAR na área de interesse**: o que não muda dentro de uma vida.
 *
 * Dito uma vez. A geração é o que invalida o desenho: uma vaga que morreu e
 * voltou entra outra vez, com uma geração nova, e o cliente larga o que tinha
 * em vez de o animar a mover-se do sítio onde caiu.
 */
export const openWorldMobEntrySchema = z
  .object({
    i: openWorldMobIdSchema,
    g: z.number().int().min(1),
    /**
     * O mapa de onde as estatísticas desta vaga saíram.
     *
     * É o **tipo visual**: o cliente resolve por ele o sprite e a escala, pelo
     * mesmo catálogo que a caça usa. Publicar o índice em vez de um id de
     * recurso é o que faz a arte dos mapas que faltam aparecer sozinha no dia
     * em que ela existir, sem mexer no protocolo.
     */
    m: z.number().int().min(1).max(50),
    /** O raio de colisão autoritativo, em milli-unidades. */
    r: z.number().int().min(1),
    /** A vida máxima em milli, como decimal. Progressão não tem teto. */
    hm: z.string().min(1).max(40),
  })
  .strict();

export type OpenWorldMobEntry = z.infer<typeof openWorldMobEntrySchema>;

/** O que um mob está a fazer, para o cliente ler a perseguição. */
export const OPEN_WORLD_MOB_STANCES = [
  /** Junto da região de origem, sem alvo. */
  'idle',
  /** A perseguir um alvo. */
  'chasing',
  /** A regressar à região de origem. */
  'returning',
] as const;

export type OpenWorldMobStance = (typeof OPEN_WORLD_MOB_STANCES)[number];

/**
 * **O estado de um mob**, a cada trama em que ele mudou.
 *
 * Posição, direção, vida e postura. **Não** o alvo: saber quem um mob está a
 * perseguir do outro lado do quadrante seria saber que alguém está lá. A
 * direção chega para a leitura, e é o que o contrato pede.
 */
export const openWorldMobStateSchema = z
  .object({
    i: openWorldMobIdSchema,
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    /** Para onde ele aponta. Normalizada, como a mira de um casco. */
    dx: z.number().int(),
    dy: z.number().int(),
    /** A vida atual em milli, como decimal. */
    h: z.string().min(1).max(40),
    s: z.enum(OPEN_WORLD_MOB_STANCES),
    /** Está envenenado. Um bit, e não a pilha: a pilha é de quem a lançou. */
    p: z.boolean(),
    /** Está a sangrar. */
    b: z.boolean(),
    /**
     * **As penalidades que uma carta lhe está a fazer** (versão 16).
     *
     * As mesmas letras de um casco, porque é o mesmo estado: um corpo lento é
     * um corpo lento, e duas grafias para ele seriam duas oportunidades de uma
     * delas ficar por desenhar. As de BENEFÍCIO não aparecem — uma criatura não
     * lança cartas — e não há campo onde pudessem aparecer.
     */
    ...openWorldCardFlagsSchema.shape,
  })
  .strict();

export type OpenWorldMobState = z.infer<typeof openWorldMobStateSchema>;

/** O que aconteceu a um mob, desenhado uma vez e esquecido. */
export const OPEN_WORLD_MOB_EVENTS = [
  /** Um golpe aterrou nele. */
  'hit',
  /** Ele atacou alguém. */
  'attack',
  /** Morreu. */
  'death',
  /** Nasceu, ou renasceu. */
  'spawn',
] as const;

export type OpenWorldMobEventKind = (typeof OPEN_WORLD_MOB_EVENTS)[number];

/** Um evento, deduplicável pelo ordinal da sua geração. */
export const openWorldMobEventSchema = z
  .object({
    i: openWorldMobIdSchema,
    n: z.number().int().min(1),
    k: z.enum(OPEN_WORLD_MOB_EVENTS),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
  })
  .strict();

export type OpenWorldMobEvent = z.infer<typeof openWorldMobEventSchema>;

/** Um mob que saiu da vista, ou que deixou de existir. */
export const openWorldMobGoneSchema = openWorldMobIdSchema;
